import { useState, useEffect } from "react";
import { toast } from "sonner";
import { WifiOff, AlertTriangle } from "lucide-react";
import dayjs from "dayjs";
import { prettyLoc, inrCompact } from "@/lib/format";
import { useDashboard } from "@/hooks/useDashboard";
import { Navbar } from "@/components/dash/Navbar";
import { FilterBar } from "@/components/dash/FilterBar";
import { OverviewSection } from "@/components/dash/OverviewSection";
import { TrendsGrid } from "@/components/dash/TrendsGrid";
import { CompareSection } from "@/components/dash/CompareSection";
import { RecordsTable } from "@/components/dash/RecordsTable";
import { Footer } from "@/components/dash/Footer";
import { MobileNav } from "@/components/dash/MobileNav";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [fy, setFy] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [coinsMode, setCoinsMode] = useState("with");
  const locParam = locations.length ? [...locations].sort().join(",") : "all";
  const { data, loading, error, offline, snapshotTs, refetch } = useDashboard(locParam, fy, dateFrom, dateTo, coinsMode);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const negLedger = (data?.location_stats || []).length
    ? data.location_stats.filter((s) => s.ledger_balance && s.ledger_balance.value < 0)
    : data?.kpis?.ledger_balance && data.kpis.ledger_balance.value < 0
      ? [{ location: locations.length === 1 ? locations[0] : (data?.meta?.locations || [])[0] || "", ledger_balance: data.kpis.ledger_balance }]
      : [];
  const al = user?.allowed_locations;
  const single =
    (Array.isArray(al) && al.length === 1) ||
    (data?.meta?.locations || []).length === 1;

  return (
    <div className="min-h-screen bg-[#FDFDFC] text-slate-900">
      <Navbar onRefresh={refetch} loading={loading} single={single} />
      <div className="pt-16">
        <FilterBar
          meta={data?.meta}
          single={single}
          locations={locations}
          setLocations={setLocations}
          fy={fy}
          setFy={setFy}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          coinsMode={coinsMode}
          setCoinsMode={setCoinsMode}
        />
        {negLedger.length > 0 && (
          <div data-testid="ledger-alert" className="bg-red-50 border-b border-red-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs font-semibold text-red-700">
              <AlertTriangle size={13} />
              {negLedger.map((s) => (
                <span key={s.location}>
                  {prettyLoc(s.location)} ledger balance {inrCompact(s.ledger_balance.value)} · as of {dayjs(s.ledger_balance.date).format("DD MMM YY")}
                </span>
              ))}
            </div>
          </div>
        )}
        {offline && (
          <div data-testid="offline-banner" className="bg-amber-50 border-b border-amber-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-2.5 flex items-center gap-2 text-xs font-semibold text-amber-800">
              <WifiOff size={13} />
              Offline — showing saved snapshot{snapshotTs ? ` from ${dayjs(snapshotTs).format("DD MMM YYYY, HH:mm")}` : ""}
            </div>
          </div>
        )}
        <OverviewSection data={data} single={single} />
        <TrendsGrid data={data} />
        {!single && <CompareSection data={data} />}
        <RecordsTable meta={data?.meta} number={single ? "03" : "04"} single={single} topLocations={locations} />
        <Footer meta={data?.meta} />
        <div className="h-16 md:hidden" />
      </div>
      <MobileNav showCompare={!single} />
    </div>
  );
}
